import { useCallback } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/sharp-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/sharp-solid-svg-icons/faChevronUp';
import { useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { useToggle } from 'ui-components/hooks/use-toggle';

import { FeaturesGrid } from '../features/features-grid';
import { messages } from './messages';
import { localizedPlanNameMessageDescriptor } from '../../plan-name-helper';
import { PageEvents, useTrackEvent } from '../../../hooks/use-track-event';
import styles from './compare-features-section.module.less';
import type { BillingPlan, SubscriptionData } from '../../../../../../prop-types/subscription-data-types.d.ts';

type Props = {
  currentBillingPlanId: string;
  plans: BillingPlan[];
  subscriptionData: SubscriptionData;
};

export const CompareFeaturesSection = ({ currentBillingPlanId, plans, subscriptionData }: Props) => {
  const intl = useIntl();
  const { activate, deactivate, isActivated: isExpanded } = useToggle(false);
  const trackClickCompareFeatures = useTrackEvent(PageEvents.SUBSCRIPTION_CHANGE_COMPARE_FEATURES_CLICK);

  const handleToggle = useCallback(() => {
    if (isExpanded) {
      deactivate();
    } else {
      activate();
    }
    trackClickCompareFeatures();
  }, [isExpanded, activate, deactivate, trackClickCompareFeatures]);

  const numberOfPlans = plans.length;
  const chevronIcon = isExpanded ? faChevronUp : faChevronDown;

  return (
    <div className={styles['compare-features-section']}>
      <Button
        ariaLabel={intl.formatMessage(messages.compareFeatures)}
        className={styles['compare-features-header']}
        onClick={handleToggle}
        theme={THEME_CONSTANTS.unstyled}
      >
        <Text textType={TextType.Label} value={messages.compareFeatures} />
        <FontAwesomeIcon icon={chevronIcon} />
      </Button>
      {isExpanded ? (
        <div className={styles['features-grid-container']} style={{ gridTemplateColumns: `200px repeat(${numberOfPlans}, 1fr)` }}>
          <div className={styles['plan-names-row']}>
            <div />
            {plans.map((plan) => {
              const { messageDescriptor, values } = localizedPlanNameMessageDescriptor(plan.name);
              const planName = intl.formatMessage(messageDescriptor, values);
              const isCurrentPlan = plan.billingPlanId === currentBillingPlanId;
              return (
                <Text
                  key={plan.billingPlanId}
                  className={classNames(styles['plan-name-cell'], { [styles['is-current-plan-column']]: isCurrentPlan })}
                  textType={TextType.Label}
                  value={planName}
                />
              );
            })}
          </div>
          <FeaturesGrid currentBillingPlanId={currentBillingPlanId} subscriptionData={subscriptionData} />
        </div>
      ) : null}
    </div>
  );
};
